import type { WishlistModelTransformed } from '~/core/entities/api/ApiWishlist';

interface WishlistsState {
	currentWishlistId: string;
	propertyCount: number;
	wishlists: WishlistModelTransformed[];
	favoritePropertyIds: string[];
}

export async function useWishlistStore() {
	// Use array instead of Set for Vue reactivity compatibility
	const registeredProperties = useState<string[]>(
		'wishlistStore:registeredProperties',
		() => []
	);
	const { $wishlistRefresh, $wishlistIsFetching } = useNuxtApp();

	const wishlistsState = useState<WishlistsState>('wishlists', () => ({
		currentWishlistId: '',
		propertyCount: 0,
		wishlists: [],
		favoritePropertyIds: []
	}));

	// Use the isFetching from the plugin
	const isFetching = computed(() => $wishlistIsFetching?.value ?? true);

	function clearWishlistsOnLogout() {
		wishlistsState.value = {
			currentWishlistId: '',
			propertyCount: 0,
			wishlists: [],
			favoritePropertyIds: []
		};
		registeredProperties.value = [];
	}

	function setCurrentWishlistId(wishlistId: string) {
		wishlistsState.value.currentWishlistId = wishlistId;
	}

	function isPropertyFavorited(propertyId: string) {
		return wishlistsState.value.favoritePropertyIds.includes(propertyId);
	}

	function registerInitialFavoriteState(
		propertyId: string,
		isFavorited: boolean
	) {
		if (registeredProperties.value.includes(propertyId)) return;

		registeredProperties.value.push(propertyId);

		if (
			isFavorited &&
			!wishlistsState.value.favoritePropertyIds.includes(propertyId)
		) {
			wishlistsState.value.favoritePropertyIds.push(propertyId);
		} else if (!isFavorited) {
			const index =
				wishlistsState.value.favoritePropertyIds.indexOf(propertyId);
			if (index > -1) wishlistsState.value.favoritePropertyIds.splice(index, 1);
		}

		wishlistsState.value.propertyCount =
			wishlistsState.value.favoritePropertyIds.length;
	}

	return {
		wishlistsState: computed(() => wishlistsState.value),
		isFetching,
		executeFetch: () => {
			if ($wishlistRefresh) {
				return $wishlistRefresh();
			}
		},
		clearWishlistsOnLogout,
		registerInitialFavoriteState,
		isPropertyFavorited,
		setCurrentWishlistId
	};
}
